/** 处理板件数据的文件 */
import { dealHoleSlotShunt } from '@/apis/paiban'
import type {
  Conflict,
  PartType,
  PointType,
  PointUpperCase,
  RectType,
} from '@/partTypes'

import ClipperLib from './clipper_unminified'
import { compShapeData, dealAllParts } from './dealPaibanData'
import { toLowerCasePoint } from './transform/graph'

const SCALE = 10000
/**
 * 扩大板件路径
 * @param offset 偏移量 正数路径扩张 负数路径内缩
 * @param path 路径
 * @returns
 */
export function expandPath(
  offset: number,
  path: PointUpperCase[]
): PointType[][] {
  // 扩张为 0 时，无需处理
  if (offset === 0) return [toLowerCasePoint(path) as PointType[]]
  window.ClipperLib.JS.ScaleUpPath(path, SCALE)
  const co = new window.ClipperLib.ClipperOffset()
  const offsetPaths = new window.ClipperLib.Paths()
  co.Clear()
  co.AddPath(
    path,
    window.ClipperLib.JoinType.jtMiter,
    window.ClipperLib.EndType.etClosedPolygon
  )
  // 执行扩张
  co.Execute(offsetPaths, offset * SCALE)
  const resPath = JSON.parse(JSON.stringify(offsetPaths))
  const result = resPath.map((path: PointUpperCase[]) =>
    (toLowerCasePoint(path) as PointType[]).map((v) => ({
      x: Number((v.x / SCALE).toFixed(3)),
      y: Number((v.y / SCALE).toFixed(3)),
    }))
  )
  //   返回处理好的路径
  return result
}

/**
 * @description 获取扩张/内缩路径内面积最大/小的路径
 * @param offset 偏移量 正数路径扩张 负数路径内缩
 * @param path 路径
 * @param areaType 面积类型 最大 最小
 * @returns 扩张/内缩路径内面积最大/小的路径
 */
export function getExpandPathByArea(
  offset: number,
  path: PointUpperCase[],
  areaType: 'max' | 'min'
): PointType[] {
  const result = expandPath(offset, path)
  const areas = result.map((v) => ClipperLib.JS.AreaOfPolygon(v))
  const maxIndex = areas.indexOf(Math.max(...areas))
  const minIndex = areas.indexOf(Math.min(...areas))
  return result[areaType === 'max' ? maxIndex : minIndex]
}

/** 根据孔槽集中面设置处理板件孔槽信息 */
export async function dealPlankHoleSlot(partList: any[], ncSetting: any) {
  const newPartList = dealAllParts(partList, ncSetting)
  const shuntPreData = await dealHoleSlotShunt({
    process_line_id: ncSetting.process_line_id ?? ncSetting.process_setting_id,
    planks: newPartList,
  })
  if (shuntPreData.status) {
    const newParts = compShapeData(shuntPreData.data.planks, partList)
    partList.forEach((part) => {
      const newPart = newParts.find(
        (p: any) => p.partUniqueId == part.partUniqueId
      )
      if (newPart) {
        Object.assign(part, newPart)
      }
    })
  }
}

/**
 * @desc 对传入的路径进行 clipper 差集运算
 * @param outerRect 外轮廓路径
 * @param cutPath 扣除路径
 * @returns
 */
export function clipperDifference(
  outerRect: PointUpperCase[][],
  cutPath: PointUpperCase[][]
) {
  const { Clipper, ClipType, PolyType, PolyFillType, Paths, JS } =
    window.ClipperLib
  outerRect = window.$copy(outerRect)
  cutPath = window.$copy(cutPath)
  outerRect.forEach((v) => JS.ScaleUpPath(v, SCALE))
  cutPath.forEach((v) => JS.ScaleUpPath(v, SCALE))
  const clipper = new Clipper()
  clipper.AddPaths(outerRect, PolyType.ptSubject, true)
  clipper.AddPaths(cutPath, PolyType.ptClip, true)
  const solution = new Paths()
  clipper.Execute(
    ClipType.ctDifference,
    solution,
    PolyFillType.pftNonZero,
    PolyFillType.pftNonZero
  )
  const resPath = JSON.parse(JSON.stringify(solution))
  const result = resPath.map((path: PointUpperCase[]) =>
    path.map((v) => ({
      X: Number((v.X / SCALE).toFixed(3)),
      Y: Number((v.Y / SCALE).toFixed(3)),
    }))
  )
  return result
}

export interface LineSlotType {
  oriPath?: {
    x: number
    y: number
    b?: number
    deep?: number
    restDeep?: number
  }[]
  path: { x: number; y: number; b?: number; deep?: number; restDeep?: number }[]
  oPath: {
    x: number
    y: number
    b?: number
    deep?: number
    restDeep?: number
  }[]
  width: number
  symbol: string
  uniqueId: string
  knifeName: string
  knifeType: string
  deep: number
  side: number
  /** 线槽轮廓 */
  polygonArr: { x: number; y: number }[][]
  conflict?: Conflict
  _conflict_uniqueId?: string
  /** "line" 沿线加工   "inward" 向内铣底   "outward" 向外铣底 */
  processMethod: 'line' | 'inward' | 'outward'
}

// 连续槽路径处理
export function dealLineSlotPath(data: { path: PointType[]; width: number }) {
  const { path, width } = data
  const isPathClosed =
    path.length > 2 &&
    path[0].x === path[path.length - 1].x &&
    path[0].y === path[path.length - 1].y

  if (isPathClosed) {
    const area = ClipperLib.JS.AreaOfPolygon(path)
    if (area > 0) {
      path.reverse()
    }
    const upperCasePath = path.map((point) => ({
      X: point.x,
      Y: point.y,
    }))
    const offset = width / 2
    // 外扩 10
    const outer = expandPath(
      offset,
      JSON.parse(JSON.stringify(upperCasePath))
    )[0]
    const inner = expandPath(
      -offset,
      JSON.parse(JSON.stringify(upperCasePath))
    )[0]
    // 异常路径无法扩张则直接返回
    if (!outer?.length || !inner?.length) {
      return
    }

    function dealPath(path: { x: number; y: number }[]) {
      const firstPoint = path[0]
      const lastPoint = path[path.length - 1]
      if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
        path.push(window.$copy(firstPoint))
      }
    }

    dealPath(outer)
    dealPath(inner)

    // outer 和 inner 的点位顺序需要相反，outer 顺时针，inner 需要逆时针，才能在各种计算中正常被认定被此块区域，内部是挖空的区域
    if (!window.ClipperLib.Clipper.Orientation(outer)) {
      outer.reverse()
    }
    if (window.ClipperLib.Clipper.Orientation(inner)) {
      inner.reverse()
    }
    // 组合 outer、inner 得到闭合环形结果
    return [outer, inner]
  } else {
    const co = new ClipperLib.ClipperOffset()
    const subj = path.map((point) => ({
      X: point.x * SCALE,
      Y: point.y * SCALE,
    }))
    // 添加路径（开口线段！）
    // 第二个参数：拐角的处理方式
    //   jtMiter / jtRound / jtSquare
    // 第三个参数：路径端点处理方式
    //   etOpenButt / etOpenSquare / etOpenRound

    co.AddPath(
      subj,
      ClipperLib.JoinType.jtSquare,
      isPathClosed
        ? ClipperLib.EndType.etClosedLine
        : ClipperLib.EndType.etOpenSquare
    )

    const solution = new ClipperLib.Paths() as any[]

    // delta 是偏移距离（这里相当于“半宽度”）
    const delta = (width * SCALE) / 2
    co.Execute(solution, delta)

    // 结果转回浮点坐标
    const result = solution.map((path: any[]) =>
      path.map((p) => ({ x: p.X / SCALE, y: p.Y / SCALE }))
    )

    const res = result[0]
    const firstPoint = res[0]
    const lastPoint = res[res.length - 1]
    if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
      res.push(window.$copy(firstPoint))
    }
    return [res]
  }
}

/**
 * @description 根据板件的原始矩形和封边信息获取板件的真实下料大小
 * @param part
 * @returns 板件的真实下料大小
 */
export function getPartRealRectByoRect(part: PartType): RectType {
  const { oRect, edgeInfo } = part
  const { width, height } = oRect
  const [left, top, right, bottom] = edgeInfo
    .split(/←|↓|→|↑/)
    .filter(Boolean)
    .map((v) => (isNaN(Number(v)) ? 0 : Number(v)))
  const realRect = {
    x: left,
    y: top,
    width: width - left - right,
    height: height - top - bottom,
  }
  return realRect
}
